import { useMemo } from "react";
import {
  Create,
  useNotify,
  SimpleForm,
  useRedirect,
  type RaRecord,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
} from "react-admin";
import { useSearchParams } from "react-router-dom";
import Typography from "@mui/material/Typography";
import BookingItsLookup from "@/components/bookings/BookingItsLookup";
import {
  buildBookingCreateDefaultValues,
  transformBookingCreate,
  validateBookingCreation,
} from "@/utils/booking-create";

function usePrefillFromRazaRequestQuery() {
  const [searchParams] = useSearchParams();
  return useMemo(() => {
    const razaRequestId = searchParams.get("razaRequestId")?.trim() || undefined;
    const organiser = searchParams.get("organiser")?.trim() || "";
    const itsNo = searchParams.get("itsNo")?.trim() || "";
    return { razaRequestId, organiser, itsNo };
  }, [searchParams]);
}

export const HallBookingsCreate = (props: Parameters<typeof Create>[0]) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { razaRequestId, organiser, itsNo } = usePrefillFromRazaRequestQuery();

  const onSuccess = (data: RaRecord) => {
    notify(`Booking created - ${data.id}`, { autoHideDuration: 5000, type: "success" });
    redirect(`/bookings/${data.id}/show`);
  };

  const defaultValues = useMemo(
    () => buildBookingCreateDefaultValues({ razaRequestId, organiser, itsNo }),
    [razaRequestId, organiser, itsNo]
  );

  return (
    <Create
      {...props}
      redirect="show"
      transform={transformBookingCreate}
      mutationOptions={{
        onSuccess,
        onError: (error: unknown) => {
          const message = error instanceof Error ? error.message : String(error);
          notify(`Could not create booking: ${message}`, { type: "error" });
        },
      }}
    >
      <SimpleForm
        warnWhenUnsavedChanges
        validate={validateBookingCreation}
        defaultValues={defaultValues}
        sx={{ maxWidth: 700 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
          Booking information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Create with core details first. Add hall lines, totals, and payments from the booking
          page.
        </Typography>

        <BookingItsLookup />
        <TextInput source="organiser" label="Organiser" fullWidth required />
        <TextInput source="phone" label="Phone" fullWidth required />

        {razaRequestId ? (
          <Typography variant="body2" color="text.secondary">
            This booking is tied to the current raza request.
          </Typography>
        ) : (
          <ReferenceInput source="razaRequestId" reference="razaRequests" perPage={100}>
            <AutocompleteInput
              label="Raza request"
              optionText={(r: RaRecord) =>
                [r.name, r.itsNo].filter(Boolean).join(" — ") || String(r.id)
              }
              fullWidth
              helperText="Required. Every booking must be linked to a raza request."
              isRequired
            />
          </ReferenceInput>
        )}
      </SimpleForm>
    </Create>
  );
};

export default HallBookingsCreate;
